import React, { useState } from 'react';
import { Table, Card, Select, DatePicker, Space, Tag, Button, Row, Col, Typography } from 'antd';
import Chart from 'react-apexcharts';

const { Option } = Select;
const { RangePicker } = DatePicker;

const EnginePredictives = () => {
    // Sample data for engine predictives
    const [engineData] = useState([
        {
            key: '1',
            engineId: 'ENG-001',
            model: 'V8 Turbo',
            temperature: 95.2,
            pressure: 45.8,
            vibration: 2.3,
            efficiency: 87.5,
            predictedFailure: 'Low Risk',
            maintenanceDue: '2025-10-15',
            status: 'Operational',
            lastUpdate: '2025-09-03 10:30'
        },
        {
            key: '2',
            engineId: 'ENG-002',
            model: 'V6 Hybrid',
            temperature: 102.1,
            pressure: 48.2,
            vibration: 3.1,
            efficiency: 82.3,
            predictedFailure: 'Medium Risk',
            maintenanceDue: '2025-09-20',
            status: 'Warning',
            lastUpdate: '2025-09-03 10:25'
        },
        {
            key: '3',
            engineId: 'ENG-003',
            model: 'V12 Performance',
            temperature: 88.7,
            pressure: 42.3,
            vibration: 1.8,
            efficiency: 91.2,
            predictedFailure: 'Low Risk',
            maintenanceDue: '2025-11-05',
            status: 'Operational',
            lastUpdate: '2025-09-03 10:35'
        },
        {
            key: '4',
            engineId: 'ENG-004',
            model: 'I4 Turbo',
            temperature: 108.5,
            pressure: 52.1,
            vibration: 4.2,
            efficiency: 75.8,
            predictedFailure: 'High Risk',
            maintenanceDue: '2025-09-08',
            status: 'Critical',
            lastUpdate: '2025-09-03 10:20'
        },
        {
            key: '5',
            engineId: 'ENG-005',
            model: 'V6 Standard',
            temperature: 93.3,
            pressure: 44.7,
            vibration: 2.7,
            efficiency: 84.6,
            predictedFailure: 'Low Risk',
            maintenanceDue: '2025-10-22',
            status: 'Operational',
            lastUpdate: '2025-09-03 10:40'
        }
    ]);

    // Table columns configuration
    const columns = [
        {
            title: 'Engine ID',
            dataIndex: 'engineId',
            key: 'engineId',
            sorter: true,
        },
        {
            title: 'Model',
            dataIndex: 'model',
            key: 'model',
        },
        {
            title: 'Temperature (°C)',
            dataIndex: 'temperature',
            key: 'temperature',
            sorter: (a, b) => a.temperature - b.temperature,
            render: (temp) => (
                <span style={{ color: temp > 100 ? '#ff4d4f' : temp > 95 ? '#faad14' : '#52c41a' }}>
                    {temp}°C
                </span>
            ),
        },
        {
            title: 'Pressure (PSI)',
            dataIndex: 'pressure',
            key: 'pressure',
            sorter: (a, b) => a.pressure - b.pressure,
        },
        {
            title: 'Vibration (Hz)',
            dataIndex: 'vibration',
            key: 'vibration',
            sorter: (a, b) => a.vibration - b.vibration,
        },
        {
            title: 'Efficiency (%)',
            dataIndex: 'efficiency',
            key: 'efficiency',
            sorter: (a, b) => a.efficiency - b.efficiency,
            render: (eff) => (
                <span style={{ color: eff > 85 ? '#52c41a' : eff > 80 ? '#faad14' : '#ff4d4f' }}>
                    {eff}%
                </span>
            ),
        },
        {
            title: 'Risk Level',
            dataIndex: 'predictedFailure',
            key: 'predictedFailure',
            render: (risk) => {
                const color = risk === 'High Risk' ? 'red' : risk === 'Medium Risk' ? 'orange' : 'green';
                return <Tag color={color}>{risk}</Tag>;
            },
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status) => {
                const color = status === 'Critical' ? 'red' : status === 'Warning' ? 'orange' : 'green';
                return <Tag color={color}>{status}</Tag>;
            },
        },
        {
            title: 'Maintenance Due',
            dataIndex: 'maintenanceDue',
            key: 'maintenanceDue',
        },
    ];

    // Chart configurations
    const temperatureChartOptions = {
        chart: {
            type: 'line',
            height: 350,
            toolbar: {
                show: false
            }
        },
        title: {
            text: 'Engine Temperature Monitoring',
            align: 'left'
        },
        stroke: {
            curve: 'smooth',
            width: 3
        },
        xaxis: {
            categories: engineData.map(engine => engine.engineId),
            title: {
                text: 'Engine ID'
            }
        },
        yaxis: {
            title: {
                text: 'Temperature (°C)'
            }
        },
        colors: ['#ff6b6b'],
        grid: {
            borderColor: '#f1f1f1'
        },
        markers: {
            size: 6
        }
    };

    const temperatureSeries = [{
        name: 'Temperature',
        data: engineData.map(engine => engine.temperature)
    }];

    const efficiencyChartOptions = {
        chart: {
            type: 'bar',
            height: 350,
            toolbar: {
                show: false
            }
        },
        title: {
            text: 'Engine Efficiency Analysis',
            align: 'left'
        },
        plotOptions: {
            bar: {
                horizontal: false,
                columnWidth: '55%',
                endingShape: 'rounded'
            },
        },
        dataLabels: {
            enabled: false
        },
        xaxis: {
            categories: engineData.map(engine => engine.engineId),
            title: {
                text: 'Engine ID'
            }
        },
        yaxis: {
            title: {
                text: 'Efficiency (%)'
            }
        },
        colors: ['#4CAF50'],
        fill: {
            opacity: 0.8
        }
    };

    const efficiencySeries = [{
        name: 'Efficiency',
        data: engineData.map(engine => engine.efficiency)
    }];

    const multiMetricChartOptions = {
        chart: {
            type: 'line',
            height: 400,
            toolbar: {
                show: false
            }
        },
        title: {
            text: 'Multi-Metric Engine Analysis',
            align: 'left'
        },
        stroke: {
            width: [3, 3, 3],
            curve: 'smooth'
        },
        xaxis: {
            categories: engineData.map(engine => engine.engineId),
            title: {
                text: 'Engine ID'
            }
        },
        yaxis: [
            {
                title: {
                    text: 'Temperature (°C) / Pressure (PSI)'
                }
            },
            {
                opposite: true,
                title: {
                    text: 'Vibration (Hz)'
                }
            }
        ],
        colors: ['#ff6b6b', '#4dabf7', '#ffa726'],
        markers: {
            size: 6
        },
        legend: {
            position: 'top'
        }
    };

    const multiMetricSeries = [
        {
            name: 'Temperature',
            data: engineData.map(engine => engine.temperature)
        },
        {
            name: 'Pressure',
            data: engineData.map(engine => engine.pressure)
        },
        {
            name: 'Vibration',
            type: 'line',
            yAxisIndex: 1,
            data: engineData.map(engine => engine.vibration)
        }
    ];

    return (
        <div className="container-fluid p-4" style={{ backgroundColor: '#f8f9fa' }}>
            {/* Header */}
            <div className="mb-4">
                <Typography.Title level={4} className="mb-3">Engine Predictives Dashboard</Typography.Title>
                <Space wrap>
                    <Select defaultValue="all" style={{ width: 120 }}>
                        <Option value="all">All Engines</Option>
                        <Option value="operational">Operational</Option>
                        <Option value="warning">Warning</Option>
                        <Option value="critical">Critical</Option>
                    </Select>
                    <RangePicker />
                    <Button type="primary">Export Data</Button>
                    <Button>Refresh</Button>
                </Space>
            </div>

            {/* Charts Section */}
            <Row gutter={[16, 16]} className="mb-4">
                <Col xs={24} lg={12}>
                    <Card title="Temperature Monitoring" bordered={false}>
                        <Chart
                            options={temperatureChartOptions}
                            series={temperatureSeries}
                            type="line"
                            height={350}
                        />
                    </Card>
                </Col>
                <Col xs={24} lg={12}>
                    <Card title="Efficiency Analysis" bordered={false}>
                        <Chart
                            options={efficiencyChartOptions}
                            series={efficiencySeries}
                            type="bar"
                            height={350}
                        />
                    </Card>
                </Col>
            </Row>

            <Row gutter={[16, 16]} className="mb-4">
                <Col xs={24}>
                    <Card title="Multi-Metric Analysis" bordered={false}>
                        <Chart
                            options={multiMetricChartOptions}
                            series={multiMetricSeries}
                            type="line"
                            height={400}
                        />
                    </Card>
                </Col>
            </Row>

            {/* Data Table */}
            <Card title="Engine Predictives Data" bordered={false}>
                <Table
                    columns={columns}
                    dataSource={engineData}
                    pagination={{
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total, range) =>
                            `${range[0]}-${range[1]} of ${total} engines`,
                    }}
                    scroll={{ x: true }}
                    size="middle"
                />
            </Card>
        </div>
    );
};

export default EnginePredictives;